import React from "react";
import { InlineMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(items) {
  return items[randInt(0, items.length - 1)];
}

function roundToStep(value, step) {
  return Math.round((value + Number.EPSILON) / step) * step;
}

function normalizeNumber(value) {
  const rounded = Math.round((Number(value) + Number.EPSILON) * 1000) / 1000;
  return Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace(".", ",");
}

function latexNumber(value) {
  return normalizeNumber(value).replace(",", "{,}");
}

function operationSymbol(operation) {
  if (operation === "difference") return "-";
  if (operation === "product") return "\\cdot";
  return "+";
}

function operationName(operation) {
  if (operation === "difference") return "różnicę";
  if (operation === "product") return "iloczyn";
  return "sumę";
}

function compute(operation, a, b) {
  if (operation === "difference") return a - b;
  if (operation === "product") return a * b;
  return a + b;
}

function makeDecimalNearRoundedValue(roundedValue, step) {
  const limit = step === 10 ? 4.8 : 0.48;
  const shifts = step === 10 ? [-4.7, -3.6, -2.8, 2.4, 3.5, 4.6] : [-0.47, -0.36, -0.28, 0.24, 0.35, 0.46];
  const shift = pickOne(shifts.filter((item) => Math.abs(item) <= limit));
  return Math.round((roundedValue + shift) * 100) / 100;
}

function makeDecimalNear(value, shift) {
  return Math.round((value + shift / 10) * 10) / 10;
}

function makeProductData() {
  const approxA = randInt(2, 9) * 10;
  const approxB = randInt(2, 9);
  const a = makeDecimalNear(approxA, pickOne([-3, -2, -1, 1, 2, 3]));
  const b = makeDecimalNear(approxB, pickOne([-4, -3, -2, 2, 3, 4]));

  return {
    a,
    b,
    approxA,
    approxB,
    answer: compute("product", approxA, approxB),
    roundingText: "większą liczbę do dziesiątek, a mniejszą do jedności",
    placeLabel: "dziesiątek i jedności",
  };
}

function makeSumOrDifferenceData(operation, step) {
  for (let i = 0; i < 80; i += 1) {
    const baseA = randInt(step === 10 ? 12 : 18, step === 10 ? 98 : 920) * step;
    const baseB = randInt(step === 10 ? 3 : 8, step === 10 ? 85 : 650) * step;
    const roundedA = Math.max(baseA, baseB);
    const roundedB = Math.min(baseA, baseB);
    const a = makeDecimalNearRoundedValue(roundedA, step);
    const b = makeDecimalNearRoundedValue(roundedB, step);
    const approxA = roundToStep(a, step);
    const approxB = roundToStep(b, step);
    const answer = compute(operation, approxA, approxB);

    if (
      Math.abs(approxA - a) > 1e-9 &&
      Math.abs(approxB - b) > 1e-9 &&
      answer >= 0 &&
      Math.abs(answer) >= step
    ) {
      return { a, b, approxA, approxB, answer };
    }
  }

  const a = step === 10 ? 163.4 : 48.7;
  const b = step === 10 ? 83.9 : 21.4;
  const approxA = roundToStep(a, step);
  const approxB = roundToStep(b, step);
  return { a, b, approxA, approxB, answer: compute(operation, approxA, approxB) };
}

function makeData(operation, step) {
  if (operation === "product") return makeProductData();

  const data = makeSumOrDifferenceData(operation, step);
  return {
    ...data,
    roundingText: step === 10 ? "obie liczby do dziesiątek" : "obie liczby do jedności",
    placeLabel: step === 10 ? "dziesiątek" : "jedności",
  };
}

export function generateTask(options = {}) {
  const operation = options.operation || pickOne(["sum", "difference", "product"]);
  const step = options.step || (operation === "product" ? 10 : pickOne([1, 10]));
  const data = makeData(operation, step);

  return {
    type: "DecimalEstimateTask",
    data: {
      ...data,
      operation,
      step,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        Oszacuj {operationName(data.operation)}{" "}
        <InlineMath
          math={`${latexNumber(data.a)} ${operationSymbol(data.operation)} ${latexNumber(data.b)}`}
        />
        , zaokrąglając {data.roundingText}.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label="oszacowany wynik"
      placeholder="np. 80"
    />
  );
}

export function checkAnswer(task, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer.parsed;
  if (parsed == null || !Number.isFinite(parsed)) return false;
  return Math.abs(parsed - task.data.answer) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={latexNumber(data.answer)} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderExplanation({ data }) {
  const symbol = operationSymbol(data.operation);
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Najpierw zaokrąglamy: {data.roundingText}.
      </p>
      <p style={{ textAlign: "center" }}>
        <InlineMath math={`${latexNumber(data.a)} \\approx ${latexNumber(data.approxA)}`} />
        {" oraz "}
        <InlineMath math={`${latexNumber(data.b)} \\approx ${latexNumber(data.approxB)}`} />
      </p>
      <p>
        <strong>Krok 2.</strong> Liczymy działanie na zaokrąglonych liczbach. Dzięki
        temu rachunek jest prostszy i można go wykonać w pamięci:
      </p>
      <p style={{ textAlign: "center" }}>
        <InlineMath
          math={`${latexNumber(data.approxA)} ${symbol} ${latexNumber(data.approxB)} = ${latexNumber(data.answer)}`}
        />
      </p>
      <p>
        <strong>Odpowiedź:</strong> oszacowany wynik to{" "}
        <InlineMath math={latexNumber(data.answer)} />.
      </p>
    </div>
  );
}
